import { plugin, debug } from 'light-component';
import dayjs from 'dayjs';
import { useEffect, useState, type ReactElement } from 'react';
import { DatePicker } from 'antd';
import { t } from '../../i18n';
import { useBreakpoint } from '../../../hooks';
import { Require } from '../component';
import styles from './style.module.less';
import type { DatePickerProps } from 'antd';
import type { Dayjs } from 'dayjs';
import type { DateRangeProps } from './types';

type RangeValue<T> = [T | null, T | null] | null;
export function WidgetDateRange(props: DateRangeProps): ReactElement {
  debug.widget('dashboard date range', props);
  const { allowClear = true } = props;
  const [dates, setDates] = useState<[(Dayjs | null), (Dayjs | null)]>([null, null]);
  const [maxDate, setMaxDate] = useState<Dayjs>();
  const { md } = useBreakpoint();

  useEffect(() => {
    if (props.maxDate == null) {
      return;
    }
    props.maxDate === '$now' ? setMaxDate(dayjs()) : setMaxDate(dayjs(props.maxDate));
  }, [props.maxDate]);

  useEffect(() => {
    const [start, end] = props.value ?? [null, null];
    const startDate = typeof (start) === 'string' ? dayjs(start) : start;
    const endDate = typeof (end) === 'string' ? dayjs(end) : end;

    if (startDate != null && endDate != null && startDate.isAfter(endDate)) {
      setDates([endDate, startDate]);
      return;
    }
    setDates([startDate, endDate]);
  }, [props.value]);

  const getYearMonth = (date: Dayjs): number => (date.year() * 12) + date.month();

  const disabledDate: DatePickerProps['disabledDate'] = (current, { from, type }) => {
    if (from != null) {
      const minDate = from.add(-props.maxRange + 1, 'days');
      const maxDate = from.add(props.maxRange - 1, 'days');

      switch (type) {
        case 'year':
          return current.year() < minDate.year() || current.year() > maxDate.year();

        case 'month':
          return (
            getYearMonth(current) < getYearMonth(minDate)
            || getYearMonth(current) > getYearMonth(maxDate)
          );

        default:
          return Math.abs(current.diff(from, 'days')) >= props.maxRange;
      }
    }

    return false;
  };

  const startDisabledDate = (current: Dayjs): boolean => {
    if (maxDate != null && current.isAfter(maxDate, 'day')) {
      return true;
    }
    if (dates[1] != null && props.maxRange != null) {
      return Math.abs(current.diff(dates[1], 'days')) >= props.maxRange;
    }
    return false;
  };

  const endDisabledDate = (current: Dayjs): boolean => {
    if (maxDate != null && current.isAfter(maxDate, 'day')) {
      return true;
    }
    if (dates[0] != null && current.isBefore(dates[0], 'day')) {
      return true;
    }
    if (dates[0] != null && props.maxRange != null) {
      return Math.abs(current.diff(dates[0], 'days')) >= props.maxRange;
    }
    return false;
  };

  // 监听日期选择器的日历变化
  function onChange(dates: RangeValue<Dayjs>): void {
    const [start, end] = dates ?? [null, null];
    const startDate = start != null ? dayjs(start).format('YYYY-MM-DD') : start;
    const endDate = end != null ? dayjs(end).format('YYYY-MM-DD') : end;
    props.onChange?.([startDate, endDate]);
  }

  function onStartChange(date: Dayjs | null): void {
    setDates([date, dates[1]]);
    const startDate = date != null ? dayjs(date).format('YYYY-MM-DD') : null;
    const endDate = dates[1] != null ? dayjs(dates[1]).format('YYYY-MM-DD') : null;
    props.onChange?.([startDate, endDate]);
  }

  function onEndChange(date: Dayjs | null): void {
    setDates([dates[0], date]);
    const startDate = dates[0] != null ? dayjs(dates[0]).format('YYYY-MM-DD') : null;
    const endDate = date != null ? dayjs(date).format('YYYY-MM-DD') : null;
    props.onChange?.([startDate, endDate]);
  }

  return (
    <div className={props.className}>
      <div className={styles.input}>
        <Require require={props.require} />
        <span className={styles.title}>{t(props.title ?? '')}</span>
      </div>
      {md === false ? (
        <div className={styles.mdRange}>
          <DatePicker
            className={styles.treeSelect}
            placeholder={t('开始时间')}
            allowClear={allowClear}
            disabled={props.disabled}
            value={dates[0]}
            disabledDate={startDisabledDate}
            maxDate={maxDate}
            onChange={onStartChange}
          />
          <DatePicker
            className={styles.treeSelect}
            placeholder={t('结束时间')}
            allowClear={allowClear}
            disabled={props.disabled}
            value={dates[1]}
            disabledDate={endDisabledDate}
            maxDate={maxDate}
            onChange={onEndChange}
          />
        </div>
      ) : (
        <DatePicker.RangePicker
          className={styles.treeSelect}
          placeholder={[t('开始时间'), t('结束时间')]}
          allowEmpty
          allowClear={allowClear}
          disabled={props.disabled}
          value={dates}
          disabledDate={disabledDate}
          onChange={onChange}
          onPanelChange={onChange}
          maxDate={maxDate}
        />
      )}
    </div>
  );
}

plugin.register({ kind: 'widget', type: 'daterange', component: WidgetDateRange });
